import type { Url } from "../data-model/primitives.js";
import type { ChapterRef } from "../data-model/novel.js";

/**
 * Base class for every error this library raises or returns. Each subclass
 * carries a stable, machine-readable `code` (see {@link DownloadErrorCode}).
 * The result/event error channels are typed as {@link KnownDownloadError}, so
 * `switch (error.code)` narrows to the concrete subclass and exposes its typed
 * fields. Custom subclasses (open extension point) use `instanceof DownloadError`.
 */
export abstract class DownloadError extends Error {
  abstract readonly code: string;
  override readonly cause?: unknown;
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.cause = options?.cause;
    this.name = this.constructor.name;
  }
}

/**
 * No registered adapter can handle the given URL (`code: "ADAPTER_NOT_FOUND"`).
 * `download()` returns this in its `{ status: "error" }` envelope;
 * `fetchMetadata()` throws it.
 */
export class AdapterNotFoundError extends DownloadError {
  readonly code = "ADAPTER_NOT_FOUND" as const;
  constructor(
    public readonly url: string,
    options?: { cause?: unknown },
  ) {
    super(`No adapter for URL: ${url}`, options);
  }
}

/** A non-retryable HTTP error response (`code: "HTTP_ERROR"`); carries `status` and `url`. */
export class HttpError extends DownloadError {
  readonly code = "HTTP_ERROR" as const;
  constructor(
    public readonly status: number,
    public readonly url: Url,
    message?: string,
    options?: { cause?: unknown },
  ) {
    super(message ?? `HTTP ${status} for ${url}`, options);
  }
}

/** The host signalled rate limiting, e.g. HTTP 429 (`code: "RATE_LIMITED"`); `retryAfterMs` if known. */
export class RateLimitedError extends DownloadError {
  readonly code = "RATE_LIMITED" as const;
  constructor(
    public readonly url: Url,
    public readonly retryAfterMs?: number,
    options?: { cause?: unknown },
  ) {
    super(`Rate limited at ${url}`, options);
  }
}

/**
 * Malformed input or an unparseable/invalid response (`code: "PARSE_ERROR"`) —
 * e.g. a bad URL, an out-of-range `chapterRange` bound, or adapter output that
 * fails schema validation. Carries optional `url` and `snippet` for debugging.
 */
export class ParseError extends DownloadError {
  readonly code = "PARSE_ERROR" as const;
  readonly url: Url | undefined;
  readonly snippet: string | undefined;
  readonly path: string | undefined;
  constructor(message: string, options?: { cause?: unknown; url?: Url; snippet?: string; path?: string }) {
    super(message, options);
    this.url = options?.url;
    this.snippet = options?.snippet;
    this.path = options?.path;
  }
}

/**
 * A single chapter could not be fetched (`code: "CHAPTER_FETCH_FAILED"`);
 * carries the failing `ref`. Surfaces in {@link DownloadPartial}'s `failures`.
 */
export class ChapterFetchError extends DownloadError {
  readonly code = "CHAPTER_FETCH_FAILED" as const;
  constructor(
    public readonly ref: ChapterRef,
    options?: { cause?: unknown },
  ) {
    super(`Chapter ${ref.index} failed: ${ref.url}`, options);
  }
}

/** A request exceeded its timeout (`code: "TIMEOUT"`). */
export class TimeoutError extends DownloadError {
  readonly code = "TIMEOUT" as const;
  constructor(
    public readonly url: Url,
    options?: { cause?: unknown },
  ) {
    super(`Timeout for ${url}`, options);
  }
}

/**
 * The operation was aborted via an `AbortSignal` (`code: "CANCELLED"`).
 * `download()` **throws** this rather than returning an error envelope.
 */
export class CancelledError extends DownloadError {
  readonly code = "CANCELLED" as const;
  constructor(options?: { cause?: unknown }) {
    super("Cancelled", options);
  }
}

/**
 * A browser-tier request was required but neither `patchright` nor `playwright`
 * is installed (`code: "BROWSER_MODULE_NOT_INSTALLED"`). The message lists the
 * install commands and the `http-only` opt-out.
 */
export class BrowserModuleNotInstalledError extends DownloadError {
  readonly code = "BROWSER_MODULE_NOT_INSTALLED" as const;
  constructor(options?: { cause?: unknown }) {
    super(
      "No headless browser module installed. Install one of:\n" +
        "  pnpm add patchright   (recommended for Cloudflare-fronted sites)\n" +
        "  pnpm add playwright   (works for non-protected pages)\n" +
        'Or pass transport: "http-only" to disable the browser tier.',
      options,
    );
  }
}

/**
 * Suggested remedy on a {@link ChallengeUnresolvedError}:
 * `"manual-solve"` (launch a headed browser and solve once) or
 * `"transport-config"` (raise the navigation timeout / use a stronger transport).
 */
export type ChallengeUnresolvedHint = "manual-solve" | "transport-config";

/**
 * A Cloudflare challenge could not be cleared within the navigation timeout
 * (`code: "CHALLENGE_UNRESOLVED"`). `hint` indicates how to recover; the message
 * spells out the concrete fix.
 */
export class ChallengeUnresolvedError extends DownloadError {
  readonly code = "CHALLENGE_UNRESOLVED" as const;
  constructor(
    public readonly url: Url,
    public readonly hint: ChallengeUnresolvedHint,
    options?: { cause?: unknown },
  ) {
    const tail =
      hint === "manual-solve"
        ? 'Try transport: { mode: "auto", browserOptions: { headed: true } } and solve the checkbox in the launched window once.'
        : "Increase transport.browserOptions.navigationTimeoutMs or switch to a stronger transport (e.g. a Browserless/ZenRows custom HttpClient).";
    super(`Cloudflare challenge could not be resolved within the navigation timeout for ${url}. ${tail}`, options);
  }
}

/** Stable, machine-readable code for every built-in {@link DownloadError}. */
export type DownloadErrorCode = KnownDownloadError["code"];

/**
 * Discriminated union of every built-in error. The result/event error channels
 * use this so `switch (error.code)` narrows to the concrete class (e.g. reaching
 * {@link HttpError.status}). A custom-adapter error that is not one of these is
 * normalized to {@link ParseError} in the returned envelope (its original is on
 * `.cause`); use `instanceof DownloadError` as the catch-all.
 */
export type KnownDownloadError =
  | AdapterNotFoundError
  | HttpError
  | RateLimitedError
  | ParseError
  | ChapterFetchError
  | TimeoutError
  | CancelledError
  | BrowserModuleNotInstalledError
  | ChallengeUnresolvedError;

/**
 * Coerce any thrown value into a {@link KnownDownloadError} without a cast.
 * Built-in errors pass through with full type; everything else (including an
 * exotic custom `DownloadError` subclass) is wrapped in a {@link ParseError}
 * with the original preserved on `cause`.
 */
export function normalizeToKnownError(err: unknown): KnownDownloadError {
  if (err instanceof AdapterNotFoundError) return err;
  if (err instanceof HttpError) return err;
  if (err instanceof RateLimitedError) return err;
  if (err instanceof ParseError) return err;
  if (err instanceof ChapterFetchError) return err;
  if (err instanceof TimeoutError) return err;
  if (err instanceof CancelledError) return err;
  if (err instanceof BrowserModuleNotInstalledError) return err;
  if (err instanceof ChallengeUnresolvedError) return err;
  if (err instanceof DownloadError) return new ParseError(err.message, { cause: err });
  return new ParseError(String(err), { cause: err });
}

/**
 * Like {@link normalizeToKnownError} but re-throws {@link CancelledError}
 * instead of returning it. Use at `download()` return sites where `CancelledError`
 * is always thrown, never placed in the error envelope.
 */
export function normalizeToEnvelopeError(err: unknown): Exclude<KnownDownloadError, CancelledError> {
  if (err instanceof CancelledError) throw err;
  const known = normalizeToKnownError(err);
  if (known instanceof CancelledError) throw known;
  return known;
}
